/**
 * In-place adjuster for a placed image's cutout shape. Renders inside the
 * selected element's chrome (which mirrors the element box exactly):
 *   • the full uncut picture as a faint ghost, so the author sees what a
 *     move would reveal (the live masked element shows through below),
 *   • the shape outline, driven by the SAME shapeCutMarkup/shapeTransform
 *     the mask and the PDF bake use,
 *   • drag anywhere to move the shape, the round handle to rotate it, the
 *     square handle to resize it. Esc or the toolbar button closes it.
 */
import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { SpreadImageElement, SpreadImageShape } from '@historyjki/shared';
import {
  shapeCutMarkup,
  shapeTransform,
  shapeMaskViewBox,
  SHAPE_OFFSET_LIMIT,
  SHAPE_SCALE_MIN,
  SHAPE_SCALE_MAX,
} from '../../utils/spreadGeometry';
import { SpreadImage } from './SpreadImage';
import { IconRotate } from './icons';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const round2 = (v: number) => Math.round(v * 100) / 100;

/** Normalize an angle to -180..180 (matches the API sanitizer). */
const normDeg = (deg: number) => {
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return round2(d);
};

/** Distance of the rotate/scale handles from the shape center, px. */
const HANDLE_DIST = 44;

interface ShapeOverlayProps {
  el: SpreadImageElement;
  shape: SpreadImageShape;
  boxAspect: number;
  onChange: (shape: SpreadImageShape) => void;
  onClose: () => void;
}

export function ShapeOverlay({ el, shape, boxAspect, onChange, onClose }: ShapeOverlayProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  /** Shape center in viewport px (from the drag-start shape, so rotation
   *  and scaling pivot on a stable point). */
  const centerPx = (s: SpreadImageShape) => {
    const r = rootRef.current?.getBoundingClientRect();
    if (!r) return { cx: 0, cy: 0, rect: null };
    return {
      cx: r.left + (0.5 + s.x / 100) * r.width,
      cy: r.top + (0.5 + s.y / 100) * r.height,
      rect: r,
    };
  };

  const track = (
    e: ReactPointerEvent,
    onMove: (ev: PointerEvent) => void,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const move = (ev: PointerEvent) => onMove(ev);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  /** Drag on the picture moves the shape. */
  const startMove = (e: ReactPointerEvent) => {
    const o = { ...shape };
    const sx = e.clientX;
    const sy = e.clientY;
    track(e, (ev) => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r) return;
      onChange({
        ...o,
        x: clamp(round2(o.x + ((ev.clientX - sx) / r.width) * 100), -SHAPE_OFFSET_LIMIT, SHAPE_OFFSET_LIMIT),
        y: clamp(round2(o.y + ((ev.clientY - sy) / r.height) * 100), -SHAPE_OFFSET_LIMIT, SHAPE_OFFSET_LIMIT),
      });
    });
  };

  /** The round handle spins the shape around its center. */
  const startRotate = (e: ReactPointerEvent) => {
    const o = { ...shape };
    const { cx, cy } = centerPx(o);
    const angleOf = (px: number, py: number) => (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
    const a0 = angleOf(e.clientX, e.clientY);
    track(e, (ev) => {
      onChange({ ...o, rot: normDeg(o.rot + angleOf(ev.clientX, ev.clientY) - a0) });
    });
  };

  /** The square handle grows/shrinks the shape around its center. */
  const startScale = (e: ReactPointerEvent) => {
    const o = { ...shape };
    const { cx, cy } = centerPx(o);
    const d0 = Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy));
    track(e, (ev) => {
      const d = Math.hypot(ev.clientX - cx, ev.clientY - cy);
      onChange({
        ...o,
        scale: clamp(round2(o.scale * (d / d0)), SHAPE_SCALE_MIN, SHAPE_SCALE_MAX),
      });
    });
  };

  const { W, H } = shapeMaskViewBox(boxAspect);
  // Handle anchors follow the shape's own rotation (like design tools):
  // rotate up (0,-1) and down-right (√½,√½) clockwise by `rot`.
  const rad = (shape.rot * Math.PI) / 180;
  const upDir = { x: Math.sin(rad), y: -Math.cos(rad) };
  const diagDir = {
    x: Math.SQRT1_2 * (Math.cos(rad) - Math.sin(rad)),
    y: Math.SQRT1_2 * (Math.sin(rad) + Math.cos(rad)),
  };
  const handleStyle = (dir: { x: number; y: number }) => ({
    left: `calc(${50 + shape.x}% + ${Math.round(dir.x * HANDLE_DIST)}px)`,
    top: `calc(${50 + shape.y}% + ${Math.round(dir.y * HANDLE_DIST)}px)`,
  });

  return (
    <div
      className="shapeov"
      data-testid="shape-overlay"
      ref={rootRef}
      onPointerDown={startMove}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="shapeov__ghost">
        <SpreadImage el={el} boxAspect={boxAspect} plainEdges />
      </div>
      <svg
        className="shapeov__outline"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        <g
          transform={shapeTransform(shape, W, H)}
          dangerouslySetInnerHTML={{ __html: shapeCutMarkup(shape.kind, W, H) }}
        />
      </svg>
      <button
        type="button"
        className="shapeov__h shapeov__h--rotate"
        title="Rotate the shape"
        aria-label="Rotate the shape"
        style={handleStyle(upDir)}
        onPointerDown={startRotate}
      >
        <IconRotate />
      </button>
      <button
        type="button"
        className="shapeov__h shapeov__h--scale"
        title="Resize the shape"
        aria-label="Resize the shape"
        style={handleStyle(diagDir)}
        onPointerDown={startScale}
      />
      <span className="shapeov__hint">drag to move · handles rotate &amp; resize · Esc when done</span>
    </div>
  );
}
